import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Box,
  Fab,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { getDeck } from '../services/decks';
import { createCard, updateCard, deleteCard } from '../services/cards';
import CardForm from '../components/CardForm';

function DeckView() {
  const { id: deckId } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [openCardForm, setOpenCardForm] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const loadDeck = useCallback(async () => {
    try {
      const data = await getDeck(deckId);
      setDeck(data);
    } catch (error) {
      console.error('Erreur lors du chargement du paquet:', error);
    }
  }, [deckId]);

  useEffect(() => {
    loadDeck();
  }, [loadDeck]);

  const handleCreateCard = async (formData) => {
    try {
      await createCard(deckId, formData);
      setOpenCardForm(false);
      loadDeck();
    } catch (error) {
      console.error('Erreur lors de la création de la carte:', error);
    }
  };

  const handleUpdateCard = async (formData) => {
    try {
      await updateCard(deckId, selectedCard.id, formData);
      setOpenCardForm(false);
      setSelectedCard(null);
      loadDeck();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la carte:', error);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette carte ?')) {
      try {
        await deleteCard(deckId, cardId);
        loadDeck();
      } catch (error) {
        console.error('Erreur lors de la suppression de la carte:', error);
      }
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    // Retirer le /api de l'URL de base pour les images
    const baseUrl = process.env.REACT_APP_API_URL.replace(/\/api$/, '');
    // Détecter si l'extension est manquante
    const hasExtension = /\.(jpg|jpeg|png|gif|webp)$/i.test(imageUrl);
    // Ajouter .jpg par défaut si pas d'extension
    const finalUrl = hasExtension ? imageUrl : `${imageUrl}.jpg`;
    return `${baseUrl}${finalUrl}`;
  };

  if (!deck) {
    return <Typography>Chargement...</Typography>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/dashboard')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{deck.name}</Typography>
      </Box>

      <Grid container spacing={3}>
        {deck.cards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.id}>
            <Card>
              <CardContent>
                {card.frontImageUrl && (
                  <Box
                    component="img"
                    src={getImageUrl(card.frontImageUrl)}
                    alt="Front"
                    sx={{
                      width: '100%',
                      height: 140,
                      objectFit: 'cover',
                      mb: 2,
                      borderRadius: 1
                    }}
                    onError={(e) => {
                      console.error('Erreur de chargement de l\'image:', {
                        originalUrl: card.frontImageUrl,
                        finalUrl: e.target.src,
                        cardId: card.id
                      });
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: card.frontColor,
                    wordBreak: 'break-word'
                  }}
                >
                  {card.front}
                </Typography>
                <Divider sx={{ my: 2 }} />
                {card.backImageUrl && (
                  <Box
                    component="img"
                    src={getImageUrl(card.backImageUrl)}
                    alt="Back"
                    sx={{
                      width: '100%',
                      height: 140,
                      objectFit: 'cover',
                      mb: 2,
                      borderRadius: 1
                    }}
                    onError={(e) => {
                      console.error('Erreur de chargement de l\'image:', {
                        originalUrl: card.backImageUrl,
                        finalUrl: e.target.src,
                        cardId: card.id
                      });
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <Typography 
                  sx={{ 
                    color: card.backColor,
                    wordBreak: 'break-word'
                  }}
                >
                  {card.back}
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton 
                  onClick={() => {
                    setSelectedCard(card);
                    setOpenCardForm(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  color="error"
                  onClick={() => handleDeleteCard(card.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => {
          setSelectedCard(null);
          setOpenCardForm(true);
        }}
      >
        <AddIcon />
      </Fab>

      <CardForm
        open={openCardForm}
        onClose={() => {
          setOpenCardForm(false);
          setSelectedCard(null);
        }}
        onSubmit={selectedCard ? handleUpdateCard : handleCreateCard}
        initialData={selectedCard}
      />
    </Container>
  );
}

export default DeckView; 